import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormLabel,
  Grid,
  Icon,
  IconButton,
} from '@material-ui/core';
import type { GridSize } from '@material-ui/core/Grid';
import Slide from '@material-ui/core/Slide';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Field, useFormikContext } from 'formik';
import moment from 'moment';
import React, { useCallback } from 'react';
import { useObservable } from 'react-use-observable';
import { CheckboxFormField } from 'v2/components/Shared/FormFields/CheckboxFormField';
import { DateRangePickerFormField } from 'v2/components/Shared/FormFields/DateRangePicker';
import { ListCheckboxFormField } from 'v2/components/Shared/FormFields/ListCheckbox';
import { MultipleTextFormField } from 'v2/components/Shared/FormFields/MultipleTextFormField';
import { SelectFormField } from 'v2/components/Shared/FormFields/SelectFormField';
import { TextFormField } from 'v2/components/Shared/FormFields/TextFormField';
import { isSameDay } from 'v2/helpers/date';
import useGetUser from 'v2/hooks/useGetUser';
import salesService from 'v2/services/sales/general';
import subscriptionsService from 'v2/services/subscriptions';
import tableService from 'v2/services/table';

import { CustomerTypes, DateTypes, ECustomerSearchTypes, PaymentTypes } from '../interfaces';
import ProductFilter from '../Product';

import type { ISubscriptionsFilter } from '../interfaces';
import type { Moment } from 'moment';

interface IValueFromInput {
  startDate: Moment;
  endDate: Moment;
}

const useStyle = makeStyles((theme) => ({
  root: {
    background: '#ffff',
    borderRadius: 4,
    width: '100%',
  },
  title: {
    fontSize: '2rem',
    fontWeight: 200,
    '& p': {
      fontSize: '1.2rem',
      fontWeight: 400,
    },
  },
  leftInput: {
    borderRadius: '4px 0px 0px 4px',
  },
  rightInput: {
    borderRadius: '0px 4px 4px 0px',
    borderWidth: '1px 1px 1px 0px',
  },
  paddingTop: {
    marginTop: '24px',
  },
  header: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}));

interface IPreset {
  id: string;
  text: string;
  start: moment.Moment;
  end: moment.Moment;
  size: GridSize;
}

const today = moment();
const presets: IPreset[] = [
  {
    id: 'week',
    text: '7 dias',
    start: moment().subtract(7, 'days'),
    end: today,
    size: 1,
  },
  {
    id: '1month',
    text: '1 mês',
    start: moment().subtract(30, 'days'),
    end: today,
    size: 2,
  },
  {
    id: '3month',
    text: '3 meses',
    start: moment().subtract(90, 'days'),
    end: today,
    size: 2,
  },
  {
    id: '6month',
    text: '6 meses',
    start: moment().subtract(180, 'days'),
    end: today,
    size: 2,
  },
];

const formatDateToString = (date: Moment): string => moment(date).format('DD/MM/YYYY');
const formatStringToDate = (text: string): Moment => moment(text, 'DD/MM/YYYY');

function Transition(props: any) {
  return <Slide direction='up' {...props} />;
}

const AdvancedFilter = () => {
  const { values, submitForm, setFieldValue, isValid, isSubmitting, resetForm } = useFormikContext<ISubscriptionsFilter>();
  const classes = useStyle({});
  const onClose = useCallback(() => subscriptionsService.setFilterIsAdvanced(false), []);
  const [open] = useObservable(() => subscriptionsService.getFilterIsAdvanced(), []);
  const user = useGetUser();

  const [isLoading] = useObservable(() => tableService.isLoading(), []);

  const [affiliates] = useObservable(() => salesService.getAffiliates(), []);
  const [contractStatus] = useObservable(() => subscriptionsService.getStatus(), []);
  const [products] = useObservable(() => subscriptionsService.getProducts(), []);

  const handleChangeDate = (value: IValueFromInput) => {
    const { startDate, endDate } = value;
    const start = formatDateToString(startDate);
    const end = formatDateToString(endDate);
    if (startDate && startDate.isValid() && endDate && endDate.isValid()) setFieldValue('date', [start, end]);
  };

  const resetFilter = useCallback(() => {
    resetForm();
    const np = products.map((prod) => {
      prod.checked = false;
      return prod;
    });
    subscriptionsService.setProducts(np);
  }, [resetForm, products]);

  const onChangeCustomerType = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setFieldValue('customer', '');
    setFieldValue('customer_type', v);
  };

  return (
    <Dialog maxWidth={'lg'} TransitionComponent={Transition} onClose={onClose} aria-labelledby='advanced-filter' open={open || false}>
      <DialogTitle id='dialog-title'>
        <>
          Filtro Avançado
          <IconButton size='small' style={{ float: 'right' }} onClick={onClose}>
            <Icon color='primary'>close</Icon>
          </IconButton>
        </>
      </DialogTitle>
      <DialogContent dividers>
        <div className={classes.root}>
          <Grid container spacing={2} alignItems='flex-start' justify='space-around'>
            <Grid item xs={12} md={1}>
              <h4>Data</h4>
            </Grid>
            <Grid item xs={12} md={2}>
              <Field id='date_type' name='date_type' label='Tipo de data' options={DateTypes} component={SelectFormField} />
            </Grid>
            <Grid item xs={12} md={2}>
              <Field
                label='Período personalizado'
                anchorDirection='right'
                id='date'
                name='date'
                showPreset={false}
                component={DateRangePickerFormField}
              />
            </Grid>
            {presets.map(({ text, start, end, size }) => {
              const isSelected = isSameDay(start, formatStringToDate(values.date[0])) && isSameDay(end, formatStringToDate(values.date[1]));
              return (
                <Grid key={text} item xs={12} md={size}>
                  <Button
                    fullWidth
                    variant={isSelected ? 'contained' : 'outlined'}
                    className={classes.paddingTop}
                    color={isSelected ? 'primary' : undefined}
                    disableRipple
                    onClick={() => handleChangeDate({ startDate: start, endDate: end })}
                  >
                    {text}
                  </Button>
                </Grid>
              );
            })}
            <Grid className={classes.header} item xs={12} md={12}>
              <Divider />
            </Grid>
            <Grid item xs={12} md={1}>
              <h4>Cliente</h4>
            </Grid>
            <Grid item xs={12} md={5}>
              <Grid container>
                <Grid item xs={4}>
                  <Field
                    id='customer_type'
                    name='customer_type'
                    inputProps={{
                      className: classes.leftInput,
                    }}
                    options={CustomerTypes}
                    component={SelectFormField}
                    onChange={onChangeCustomerType}
                  />
                </Grid>
                <Grid item xs={8}>
                  <Field
                    id='customer'
                    name='customer'
                    component={TextFormField}
                    inputProps={{
                      className: classes.rightInput,
                    }}
                    placeholder={
                      values.customer_type === ECustomerSearchTypes.DOCUMENT
                        ? 'Insira o CPF/CNPJ'
                        : values.customer_type === ECustomerSearchTypes.EMAIL
                        ? 'Insira o e-mail'
                        : 'Insira o nome'
                    }
                    mask={values.customer_type === ECustomerSearchTypes.DOCUMENT ? 'document' : null}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={6}></Grid>
            <Grid className={classes.header} item xs={12} md={12}>
              <Divider />
            </Grid>
            <Grid item xs={12} md={1}>
              <h4>Produtos</h4>
            </Grid>
            <Grid item xs={12} md={5}>
              <Field id='product_id' name='product_id' component={ProductFilter} />
            </Grid>
            <Grid item xs={12} md={6}></Grid>
            <Grid className={classes.header} item xs={12} md={12}>
              <Divider />
            </Grid>
            <Grid item xs={12} md={1}>
              <h4>Vendas</h4>
            </Grid>
            <Grid item xs={12} md={5}>
              <Field
                multiline
                rows={8}
                label='Contratos'
                id='contract_id'
                name='id'
                placeholder={'Insira os contratos separados por vírgula (EX: 1234,54678,234543)'}
                onlyNumbers
                component={MultipleTextFormField}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Field
                id='status'
                name='status'
                disabled={!contractStatus?.length}
                options={contractStatus}
                label='Status do contrato'
                component={ListCheckboxFormField}
                formStyle={{
                  display: 'flex',
                  flexDirection: 'column',
                  flexWrap: 'wrap',
                  maxHeight: 260,
                  width: '100%',
                }}
                formControlStyle={{ width: '50%' }}
              />
            </Grid>
            <Grid item xs={11}>
              <Divider />
            </Grid>
            <Grid item xs={12} md={5}>
              <FormLabel>Filtros específicos</FormLabel>
              <Field id='open_invoices' name='open_invoices' label='Contratos com fatura em aberto (além da vigente)' component={CheckboxFormField} />
              {user?.negociate_invoice && (
                <Field id='negotiated_invoices' name='negotiated_invoices' label='Contratos com faturas negociadas' component={CheckboxFormField} />
              )}
            </Grid>
            <Grid item xs={12} md={3}>
              <Field id='payment_method' name='payment_method' options={PaymentTypes} label='Tipo de pagamento' component={ListCheckboxFormField} />
            </Grid>
            <Grid className={classes.header} item xs={12} md={12}>
              <Divider />
            </Grid>
            <Grid item xs={12} md={1}>
              <h4>Afiliados</h4>
            </Grid>
            <Grid item xs={12} md={5}>
              <Field id='affiliate_id' name='affiliate_id' multiple options={affiliates || []} label='Afiliado' component={SelectFormField} />
            </Grid>
            <Grid item xs={12} md={6}></Grid>
            <Grid className={classes.header} item xs={12} md={12}>
              <Divider />
            </Grid>
            <Grid item xs={12} md={1}>
              <h4>Outros</h4>
            </Grid>
            <Grid item xs={12} md={5}>
              <Field
                multiline
                rows={8}
                label='Faturas'
                id='invoice_id'
                name='invoice_id'
                placeholder='Insira as faturas separadas por vírgula (EX: 123, 456, 798)'
                onlyNumbers
                component={MultipleTextFormField}
              />
            </Grid>
            <Grid item xs={12} md={6}></Grid>
          </Grid>
        </div>
      </DialogContent>
      <DialogActions>
        <Button id='clean-filters-button' onClick={resetFilter}>
          Limpar filtros
        </Button>
        <Button
          id='advanced-filter-button'
          variant='contained'
          disabled={!isValid || isSubmitting || isLoading}
          color='secondary'
          type='submit'
          onClick={submitForm}
        >
          <span id='advanced-filter-button-name'>Filtrar</span>
          {(isLoading || isSubmitting) && <CircularProgress size={10} style={{ color: '#192542' }} />}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
export default AdvancedFilter;
